import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trialApi } from "../api/trial";
import { trackEvent } from "../lib/analytics";

export default function TrialPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [trial, setTrial] = useState(null); // { has_trial, licence_key, activated, issued_at }
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t, i18n } = useTranslation("dashboard");

  const fetchTrial = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await trialApi.get();
      setTrial(resp.data);
    } catch {
      setError(t("trial.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrial();
  }, []);

  const handleStart = async () => {
    setStarting(true);
    setError("");
    try {
      const resp = await trialApi.start();
      trackEvent("trial_start");
      setTrial(resp.data);
    } catch {
      setError(t("trial.startError"));
    } finally {
      setStarting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trial.licence_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available — ignore silently
    }
  };

  if (loading) {
    return (
      <div className="feature-card rounded-lg p-8 mb-6">
        <p className="text-sm text-[#A1A1AA]">{t("trial.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-card rounded-lg p-8 mb-6">
        <p className="text-sm text-[#A1A1AA] mb-4">{error}</p>
        <button
          onClick={fetchTrial}
          className="btn-primary px-6 py-2 rounded text-sm font-semibold"
        >
          {t("trial.retry")}
        </button>
      </div>
    );
  }

  if (!trial?.has_trial) {
    return (
      <div className="feature-card rounded-lg p-8 mb-6">
        <h2 className="font-clash text-xl text-white mb-2">{t("trial.freeTrialHeading")}</h2>
        <p className="text-sm text-[#A1A1AA] mb-4">
          {t("trial.freeTrialDesc")}
        </p>
        <button
          onClick={handleStart}
          disabled={starting}
          className="btn-primary px-8 py-3 rounded text-sm font-semibold disabled:opacity-50"
        >
          {starting ? t("trial.starting") : t("trial.startButton")}
        </button>
      </div>
    );
  }

  return (
    <div className="feature-card rounded-lg p-8 mb-6">
      <h2 className="font-clash text-xl text-white mb-2">{t("trial.licenceHeading")}</h2>
      <div className="flex items-center gap-3 flex-wrap mt-3">
        <code className="flex-1 min-w-0 break-all bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 font-mono text-sm text-white">
          {trial.licence_key}
        </code>
        <button
          onClick={handleCopy}
          className="btn-primary px-6 py-3 rounded text-sm font-semibold"
        >
          {copied ? t("trial.copied") : t("trial.copy")}
        </button>
      </div>
      {trial.status === "active" && (
        <p className="text-sm mt-4 text-[#66FF99]">
          {trial.expires_at
            ? t("trial.activeWithDate", {
                count: trial.days_remaining,
                date: new Date(trial.expires_at).toLocaleDateString(i18n.language),
              })
            : t("trial.active", { count: trial.days_remaining })}
        </p>
      )}
      {trial.status === "expired" && (
        <p className="text-sm mt-4 text-[#FFAA66]">{t("trial.expired")}</p>
      )}
      {trial.status !== "active" && trial.status !== "expired" && (
        <p className="text-sm text-[#A1A1AA] mt-4">
          {t("trial.notActivated")}
        </p>
      )}
    </div>
  );
}
